// React
import * as React from "react";
import _ from 'lodash';
import QRCode from 'qrcode.react';
import { Message, Container, Header, Content } from 'rsuite';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';
import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { WebThingPort, WebThing } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { DebugEx } from '@Utils/DebugEx';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
// =================================================================================================
//Data port validation
export function DataInputValidator() {
    return JsonSchemaValidator.GetInstance(
        'DataInputValidator',
        {
            type: "object",
            properties: {
                value: { type: "string" },
                size: { type: "number" },
                bgColor: { type: "string" },
                fgColor: { type: "string" },
                level: { enum: ['L', 'M', 'Q', 'H'] },
                includeMargin: { type: "boolean" },
                renderAs: { enum: ['canvas', 'svg'] },
                imageSettings: {
                    type: ["object"],
                    properties: {
                        src: { type: "string" },
                        x: { type: "number" },
                        y: { type: "number" },
                        height: { type: "number" },
                        width: { type: "number" },
                        excavate: { type: "boolean" }
                    }
                },
            },
            required: ['value']
        }
    );
}
// -------------------------------------------------------------------------------------------------
const enum PortKeys {
    Data = "Data",
}
//--------------------------------------------------------------------------------------------------
export interface ImageSettings {
    src: string;
    x?: number;
    y?: number;
    height: number;
    width: number;
    excavate: boolean;
}
// -------------------------------------------------------------------------------------------------
export interface QRCodeViewerData {
    value?: string;
    size?: number;
    bgColor?: string;
    fgColor?: string;
    level?: "L" | "M" | "Q" | "H";
    includeMargin?: boolean;
    renderAs?: "canvas" | "svg";
    imageSettings?: ImageSettings;
    verticalAligment?: string;
    horizontalAligment?: string;
}
// -------------------------------------------------------------------------------------------------
export interface QRCodeViewerState {
    Data: QRCodeViewerData;
    horizontalAligment: string;
    verticalAligment: string;
}
// -------------------------------------------------------------------------------------------------
export class QRCodeViewer extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<QRCodeViewerData>, QRCodeViewerState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<QRCodeViewerData>) {
        super(props);
        this.state = {
            Data: {
                value: "",
            },
            horizontalAligment: this.props.WidgetData.horizontalAligment,
            verticalAligment: this.props.WidgetData.verticalAligment,
        }
    }
    // --------------------------------------------------------------------------------------------
    componentDidMount() {
        this.UpdateDataFromPort(this.props);
    }
    // --------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<QRCodeViewerData>) {
        if (prevProps.PortVersion !== this.props.PortVersion || prevProps.WidgetData !== this.props.WidgetData)
            this.UpdateDataFromPort(this.props);
    }
    // ---------------------------------------------------------------------------------------------
    UpdateDataFromPort(props: WidgetsThingsStructs.PartialIWidgetThingsProps<QRCodeViewerData>) {
        const { verticalAligment, horizontalAligment } = this.props.WidgetData;
        if (!isDefined(this.props.PortsStates))
            return;

        let port = this.props.PortsStates[PortKeys.Data];
        if (!isDefined(port))
            return;

        let data: QRCodeViewerData = port.State;

        if (!_.isEmpty(data)) {
            DebugEx.TraceLog("[Port-Config]")
            this.setState({
                Data: data,
                horizontalAligment: horizontalAligment,
                verticalAligment: verticalAligment,
            });

            // Stop the set here
            return;
        }

        //Default null
        this.setState({
            horizontalAligment: horizontalAligment,
            verticalAligment: verticalAligment
        });

    }
    // ---------------------------------------------------------------------------------------------
    render() {
        const { Data, horizontalAligment, verticalAligment } = this.state;
        const { width, height } = this.props;

        let size = isDefined(Data.size) ? Data.size : Math.min(width, IsNullOrEmpty(Data.value) ? 0.9 * height : height);

        return (
            <Container style={{ height: height, width: width }}>
                {
                    IsNullOrEmpty(Data.value) ?
                        <Header style={{ position: 'relative', overflow: 'visible', zIndex: 2, padding: 0, margin: 0 }}>
                            <Message showIcon type="warning" description="Value is empty!" />
                        </Header>
                        : null
                }
                <Content style={{ display: "flex", flexFlow: 'wrap', justifyContent: horizontalAligment, alignItems: verticalAligment }} >
                    <QRCode
                        value={isDefined(Data.value) ? Data.value : ""}
                        size={size}
                        bgColor={Data.bgColor}
                        fgColor={Data.fgColor}
                        level={Data.level}
                        includeMargin={Data.includeMargin}
                        renderAs={Data.renderAs}
                        imageSettings={Data.imageSettings}
                    />
                </Content>
            </Container>
        );
    }
}
// =================================================================================================
class QRCodeViewerSettings extends React.PureComponent<IWidgetSettingsProps<QRCodeViewerData>> {
    constructor(props: IWidgetSettingsProps<QRCodeViewerData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<QRCodeViewerData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.DropDown,
                        label: "Align Horizontal",
                        dataPath: "horizontalAligment",
                        tooltip: 'Set the horizontal position of the QRcode (left, middle, right)',
                        items: [
                            { value: "flex-start", label: 'Left' },
                            { value: 'center', label: 'Middle' },
                            { value: 'flex-end', label: 'Right' },
                        ]
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Align Vertical",
                        dataPath: "verticalAligment",
                        tooltip: 'Set the vertical position of the QRcode (top, middle, bottom)',
                        items: [
                            { value: "flex-start", label: 'Top' },
                            { value: 'center', label: 'Middle' },
                            { value: 'flex-end', label: 'Bottom' },
                        ]
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
        />;
    }
}
// ============================a=====================================================================
export class QRCodeViewerWidget extends IWidgetThingsClass<QRCodeViewerData> {
    Render(key: string, width: number, height: number): React.ReactNode {
        return <QRCodeViewer />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <QRCodeViewerSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: QRCodeViewerData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: 'Data',
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'QR Data',
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data)
        ));
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as QRCodeViewerData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): QRCodeViewerData {
        return {
            // TODO: Set the default values for the widget
            value: "",
            horizontalAligment: "center",
            verticalAligment: "center"
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return QRCodeViewerWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'QR Viewer ',
            WidgetTitle: '',
            Key: 'QRCodeViewerWidget',
            Type: 'QRCodeViewerWidget',
            Data: null,
            Description: 'QRCodeViewerWidget Description',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/line_chart.svg"
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        switch (key) {
            case PortKeys.Data:
                {
                    console.log("[DataInputValidator][result]", DataInputValidator)
                    return DataInputValidator().DataValidateString(value);

                }
            default:
                return { valid: true, errors: '' };
        }
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: QRCodeViewerData): QRCodeViewerData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}